import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Carousel from "../Carousel/Carousel";
import SectionHead from "../SectionHead/SectionHead";
import gcloudPhoto from "../../assets/redesign/home/premio-gcloud-equipe.jpg";
import ciscoPhoto from "../../assets/redesign/home/premio-cisco-top3.jpg";
import googleCloud from "../../assets/redesign/logos/google-cloud-wordmark.svg";
import googleEducation from "../../assets/redesign/logos/google-for-education.svg";
import rnp from "../../assets/logos/rnp-logo.png";
import redHat from "../../assets/redesign/logos/red-hat.svg";
import cisco from "../../assets/logos/cisco.png";
import "./Awards.css";

type Award = {
  key: string;
  logo: string;
  alt: string;
  /** Foto do prêmio. Sem foto, o logo ocupa o lugar dela. */
  photo?: { src: string; alt: string };
  title?: string;
  text: string;
  cta?: { label: string; to: string };
};

// Um prêmio por vez em destaque no meio; os outros vão tomando o lugar dele (autoplay do Carousel).
// Os dois grandes prêmios abrem a lista.
const AWARDS: Award[] = [
  {
    key: "gcloud",
    logo: googleCloud,
    alt: "Google Cloud",
    photo: {
      src: gcloudPhoto,
      alt: "Equipe da Calriz com o troféu e o certificado de Google Cloud Partner of the Year no Google Cloud Summit Brasil",
    },
    title: "Partner of the Year 2024 – Public Sector",
    text: "Reconhecimento do Google Cloud à nossa atuação e ao impacto na transformação digital do setor público na América Latina.",
    cta: { label: "Saiba mais", to: "/sobre" },
  },
  {
    key: "cisco",
    logo: cisco,
    alt: "Cisco",
    photo: { src: ciscoPhoto, alt: "Troféu TOP 3 SP Routing Excellence 2025 da Calriz" },
    title: "TOP 3 – SP Routing Excellence 2025",
    text: "Prêmio recebido no Cisco Partner Fórum 2026, entre os parceiros de destaque em roteamento para provedores de serviço.",
    cta: { label: "Saiba mais", to: "/blog/12" },
  },
  {
    key: "rnp",
    logo: rnp,
    alt: "RNP",
    title: "Parceiro estratégico em projetos de conectividade e educação digital",
    text: "Com a Rede Nacional de Ensino e Pesquisa, levamos formação em tecnologia e inteligência artificial a mais de 4 milhões de alunos, professores e pesquisadores em 1.831 universidades.",
    cta: { label: "Saiba mais", to: "/parceiro-pedagogico" },
  },
  {
    key: "google_education",
    logo: googleEducation,
    alt: "Google for Education",
    title: "Destaque em inovação para a educação no Brasil",
    text: "Formação de educadores e o Programa Aluno Tutor levaram Bertioga a Município Referência Google for Education no Brasil, com projeção internacional.",
    cta: { label: "Saiba mais", to: "/parceiro-pedagogico" },
  },
  {
    key: "red_hat",
    logo: redHat,
    alt: "Red Hat",
    title: "Parceria em soluções de infraestrutura aberta e segura",
    text: "Implantamos e sustentamos ambientes com Red Hat OpenShift, integrados às nossas operações de redes e ao NOC contínuo.",
  },
];

export default function Awards() {
  const { t } = useTranslation();
  const title = t("home.awards_title", "Premiações e reconhecimentos");

  return (
    <section className="cz-awards" aria-labelledby="cz-awards-title">
      <div className="cz-awards__inner">
        <Carousel
          variant="center"
          arrows="top"
          dots
          autoplay={8000}
          label={title}
          head={
            <SectionHead
              id="cz-awards-title"
              title={title}
              text={t("home.awards_text", "Nosso compromisso com a excelência é reconhecido por parceiros globais e pelo mercado.")}
            />
          }
        >
          {AWARDS.map((award) => {
            const k = `home.awards.${award.key}`;
            const awardTitle = award.title && t(`${k}.title`, award.title);
            const text = t(`${k}.text`, award.text);
            return (
              <article key={award.key} className={`cz-award${award.photo ? "" : " cz-award--logo"}`}>
                <div className="cz-award__media">
                  {award.photo ? (
                    <img
                      src={award.photo.src}
                      alt={t(`${k}.img_alt`, award.photo.alt)}
                      className={`cz-award__photo cz-award__photo--${award.key}`}
                      loading="lazy"
                    />
                  ) : (
                    <img src={award.logo} alt={award.alt} className={`cz-award__big-logo cz-award__big-logo--${award.key}`} />
                  )}
                </div>
                <div className="cz-award__body">
                  {award.photo && (
                    <img src={award.logo} alt={award.alt} className={`cz-award__logo cz-award__logo--${award.key}`} />
                  )}
                  {awardTitle ? (
                    <>
                      <h3 className="cz-award__title">{awardTitle}</h3>
                      <p className="cz-award__text">{text}</p>
                    </>
                  ) : (
                    // sem título próprio, a frase do reconhecimento faz o papel de título
                    <h3 className="cz-award__title">{text}</h3>
                  )}
                  {award.cta && (
                    <Link to={award.cta.to} className="cz-outline-btn cz-outline-btn--sm cz-outline-btn--pink cz-award__cta">
                      {t(`${k}.cta`, award.cta.label)}
                      <ArrowRight size={15} aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </Carousel>
      </div>
    </section>
  );
}
